import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePendingSave } from 'context/PendingSaveContext';
import useNavigationContext from 'hooks/context/nav-context';

/**
 * The brands screen shows one form PER BRAND, and each stores itself. The
 * leave question knows one screen at a time, so the forms report here and
 * this speaks for all of them: leaving first stores what every form still
 * has on its way, and asks when any of them could not be stored.
 */
interface BrandSaver {
  /** A save of this form did not go through. */
  failing: boolean;
  saveNow: () => Promise<boolean>;
  isKept: () => boolean;
  abandon: () => void;
}

interface BrandSaversApi {
  report: (brandId: number, saver: BrandSaver | null) => void;
  /** The person changed something in this brand's form. */
  touched: (brandId: number) => void;
  /** Is this the form that was changed last? Ctrl/Cmd+Z is its own then. */
  isLastTouched: (brandId: number) => boolean;
  /** This brand was stored during this visit. */
  wasStored: (brandId: number) => void;
}

const BrandSaversContext = createContext<BrandSaversApi>({
  report: () => undefined,
  touched: () => undefined,
  isLastTouched: () => true,
  wasStored: () => undefined,
});

export const useBrandSavers = (): BrandSaversApi =>
  useContext(BrandSaversContext);

export const BrandSavers: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const savers = useRef(new Map<number, BrandSaver>());
  const lastTouched = useRef<number | null>(null);
  const storedBrands = useRef(new Set<number>());
  const [anyFailing, setAnyFailing] = useState(false);
  const { setPending, registerAbandon, registerSaveNow } = usePendingSave();

  const report = useCallback<BrandSaversApi['report']>((brandId, saver) => {
    if (saver) savers.current.set(brandId, saver);
    else savers.current.delete(brandId);
    setAnyFailing([...savers.current.values()].some((one) => one.failing));
  }, []);

  const api = useMemo<BrandSaversApi>(
    () => ({
      report,
      touched: (brandId) => {
        lastTouched.current = brandId;
      },
      isLastTouched: (brandId) => lastTouched.current === brandId,
      wasStored: (brandId) => {
        storedBrands.current.add(brandId);
      },
    }),
    [report]
  );

  useEffect(() => {
    setPending(anyFailing);
    registerAbandon(() =>
      [...savers.current.values()].forEach((one) => one.abandon())
    );
    registerSaveNow(
      async () => {
        const kept = await Promise.all(
          [...savers.current.values()].map((one) => one.saveNow())
        );
        return kept.every(Boolean);
      },
      () => [...savers.current.values()].every((one) => one.isKept())
    );
    return () => {
      setPending(false);
      registerAbandon(null);
      registerSaveNow(null);
    };
  }, [anyFailing, setPending, registerAbandon, registerSaveNow]);

  /*
   * The rest of the app reads the brands from one shared place. It is
   * brought up to date when this screen is left, so that a job page of the
   * brand, or this screen on coming back, shows what was stored (scene 4).
   *
   * - Only when something WAS stored, and the open campaign only when it is
   *   of a brand that was stored: reading the campaign again blanks the
   *   screen the person arrives on for a moment.
   * - A moment AFTER leaving, not at once: with the browser's Back button
   *   nothing waits for a store that is still on its way, and read at once
   *   the list could come back with the old values, which the next change
   *   on this screen would then store over the new ones (review of the slice).
   */
  const { refetchBrands, refetchCurrentVacancy, currentVacancy } =
    useNavigationContext();
  const refreshOnLeaving = useRef(() => undefined as void);
  refreshOnLeaving.current = () => {
    const stored = new Set(storedBrands.current);
    if (!stored.size) return;
    const campaignBrand = currentVacancy?.brand_id;
    window.setTimeout(() => {
      refetchBrands();
      if (campaignBrand && stored.has(campaignBrand)) refetchCurrentVacancy();
    }, 1500);
  };
  useEffect(() => () => refreshOnLeaving.current(), []);

  return (
    <BrandSaversContext.Provider value={api}>
      {children}
    </BrandSaversContext.Provider>
  );
};
